<script setup>
// NOTICE: KAPTION-INTEGRATION
import { computed, onMounted, onUnmounted } from 'vue';
import { emitter } from 'shared/helpers/mitt';

import MessageMeta from '../MessageMeta.vue';

import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';
import { useUserLanguage } from 'dashboard/composables/useUserLanguage';
import { useAccount } from 'dashboard/composables/useAccount';
// NOTICE: KAPTION-INTEGRATION - Use composable for consistent preferences + master toggle check
import { useKaptionAIFeaturesPreferences } from 'dashboard/composables/useKaptionAIFeaturesPreferences';
import { useUISettings } from 'dashboard/composables/useUISettings';
// NOTICE: KAPTION-INTEGRATION - Use message formatter to properly render mentions in reply previews
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
// NOTICE: KAPTION-INTEGRATION - template-aware quoted-reply preview (whatsapp-sent-template-tracking.RENDERING.5)
import { extractTemplateBody } from '../kaption/templatePreview';

import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

// NOTICE: KAPTION-INTEGRATION - isBotPrivateNote is provided by Message.vue context
const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  shouldGroupWithPrevious,
  conversationId,
  isBotPrivateNote,
  isGroupConversation,
  sender,
} = useMessageContext();
const { t } = useI18n();
const { userLanguage } = useUserLanguage();
const { currentAccount } = useAccount();
const { getPlainText } = useMessageFormatter();

// NOTICE: KAPTION-INTEGRATION - Use composable for per-conversation preferences
// This ensures consistent localStorage keys across all components
// conversationId comes from useMessageContext() above
const { translationEnabled, setTranslation } =
  useKaptionAIFeaturesPreferences(conversationId);

// NOTICE: KAPTION-INTEGRATION - Get master toggle from uiSettings (topbar setting)
const { uiSettings } = useUISettings();
const masterToggleEnabled = computed(
  () => uiSettings.value?.kaption_live_translation_enabled ?? true
);

// NOTICE: KAPTION-INTEGRATION - Combined check: master toggle AND per-conversation preference
// This ensures translations are hidden when the global toggle is OFF
const isTranslationEnabled = computed(() => {
  return masterToggleEnabled.value && translationEnabled.value;
});

// NOTICE: KAPTION-INTEGRATION - Listen for translation toggle events from KaptionAIFeaturesPill
// Update the composable's ref when the event fires to maintain reactivity
const handleTranslationToggle = ({
  conversationId: eventConversationId,
  enabled,
}) => {
  if (eventConversationId === conversationId.value) {
    setTranslation(enabled);
  }
};

onMounted(() => {
  emitter.on('kaption-translation-toggled', handleTranslationToggle);
});

onUnmounted(() => {
  emitter.off('kaption-translation-toggled', handleTranslationToggle);
});

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-green-3 text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-green-4 text-n-slate-12',
  // NOTICE: KAPTION-INTEGRATION - Kaption AI messages: green background + purple glow + thin border
  [MESSAGE_VARIANTS.KAPTION_AI]:
    'bg-n-green-3 text-n-slate-12 shadow-[0_0_12px_rgba(124,58,237,0.33)] border border-kpt-copilot/[0.16]',
  // NOTICE: KAPTION-INTEGRATION - Kaption AI private notes: amber background + purple glow + thin border
  [MESSAGE_VARIANTS.KAPTION_AI_PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold shadow-[0_0_12px_rgba(124,58,237,0.33)] border border-kpt-copilot/[0.16]',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-green-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  // NOTICE: KAPTION-INTEGRATION - Special styling for bot private notes
  if (isBotPrivateNote.value) {
    // No background, smaller text, 80% opacity, white/gray text, full width, centered
    // Override prose max-width to allow full width
    return [
      'text-[11px] opacity-80 text-n-slate-11 [&_p]:!text-[11px] [&_p]:!text-n-slate-11 [&_*]:!text-[11px] [&_.prose]:!max-w-none [&_.prose-bubble]:!max-w-none',
      'w-full max-w-full text-center', // Full width for bot notes, centered text
    ];
  }

  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

// NOTICE: KAPTION-INTEGRATION - Show sender name for group conversations and agent name display
const showAgentNameEnabled = computed(
  () => !!currentAccount.value?.settings?.show_agent_name_in_conversations
);

const shouldShowSenderName = computed(() => {
  if (!sender?.value?.name) return false;

  // NOTICE: KAPTION-INTEGRATION - WhatsApp-style grouping: suppress sender name on
  // consecutive messages from the same sender (only show on first message in a sequence)
  if (shouldGroupWithPrevious?.value) return false;

  // NOTICE: KAPTION-INTEGRATION - When show_agent_name_in_conversations is enabled,
  // show sender names on outgoing agent messages and private notes.
  // Contact names on incoming messages only show in group conversations (below).
  if (showAgentNameEnabled.value) {
    if (
      variant.value === MESSAGE_VARIANTS.AGENT ||
      variant.value === MESSAGE_VARIANTS.PRIVATE
    ) {
      return true;
    }
  }

  // Group conversations: show sender name for incoming messages (contacts)
  if (
    isGroupConversation?.value &&
    orientation.value === ORIENTATION.LEFT &&
    variant.value === MESSAGE_VARIANTS.USER
  ) {
    return true;
  }

  return false;
});

const senderName = computed(() => sender?.value?.name || '');

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const {
    content: replyContent,
    attachments,
    contentAttributes,
  } = inReplyTo.value;

  // NOTICE: KAPTION-INTEGRATION
  // Show translated text in reply preview if available and translations are enabled
  // Uses isTranslationEnabled which checks both master toggle AND per-conversation preference
  if (isTranslationEnabled.value && contentAttributes) {
    // Check new format first (multiple translations)
    const translations = contentAttributes.kaptionTranslations;
    if (translations && typeof translations === 'object') {
      // Try user's language first
      if (userLanguage?.value && translations[userLanguage.value]?.text) {
        return getPlainText(translations[userLanguage.value].text);
      }
      // Fallback to account locale
      const locale = currentAccount?.value?.locale;
      if (locale && typeof locale === 'string') {
        const accountLocale = locale.split('_')[0]?.toLowerCase?.();
        if (accountLocale && translations[accountLocale]?.text) {
          return getPlainText(translations[accountLocale].text);
        }
      }
      // Fallback to first available translation
      const firstKey = Object.keys(translations)[0];
      if (firstKey && translations[firstKey]?.text) {
        return getPlainText(translations[firstKey].text);
      }
    }
    // Fallback to old format (single translation) for backwards compatibility
    const translatedContent = contentAttributes.kaptionTranslation?.text;
    if (translatedContent) return getPlainText(translatedContent);
  }

  // NOTICE: KAPTION-INTEGRATION
  // whatsapp-sent-template-tracking.RENDERING.5 — a quoted message that is a
  // materialized WhatsApp template stores only the template name/slug in
  // `content` for rows created before the body was persisted to content; show
  // the interpolated template body instead of the slug.
  const templateBody = extractTemplateBody(contentAttributes);
  if (templateBody) return getPlainText(templateBody);

  // Use getPlainText to properly render mentions (e.g., [@John](mention://user/123) -> @John)
  if (replyContent) return getPlainText(replyContent);
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    :class="[
      isBotPrivateNote ? 'text-[11px]' : 'text-sm',
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL && !isBotPrivateNote,
        'w-full max-w-full': isBotPrivateNote,
      },
    ]"
  >
    <!-- Sender name header for group conversations -->
    <div
      v-if="shouldShowSenderName"
      class="text-xs font-semibold text-n-blue-11 mb-1 capitalize"
    >
      {{ senderName }}
    </div>
    <div
      v-if="inReplyTo"
      class="p-2 -mx-1 mb-2 rounded-lg cursor-pointer bg-n-alpha-black1"
      @click="scrollToMessage"
    >
      <span class="break-all line-clamp-2">
        {{ replyToPreview }}
      </span>
    </div>
    <slot />
    <MessageMeta
      v-if="shouldShowMeta"
      :class="[
        isBotPrivateNote ? 'justify-center' : flexOrientationClass,
        variant === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : '',
        isBotPrivateNote
          ? 'text-[8px] text-n-slate-11 opacity-80'
          : variant === MESSAGE_VARIANTS.PRIVATE
            ? 'text-n-amber-12/50'
            : 'text-n-slate-11',
      ]"
      class="mt-2"
    />
  </div>
</template>
